import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Markdown from "@/components/Markdown";
import { RANGER_AVATAR } from "@/lib/constants";

export default function GuideDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => {
      setA(r.data);
      const slugs = r.data.related_business_slugs || [];
      if (slugs.length) api.get("/businesses").then((res) => setRelated(res.data.filter((b) => slugs.includes(b.slug)))).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Story not found. <Link to="/guides" className="text-[#FF0055]">Back to guides</Link></div>;
  if (!a) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const absImg = a.image?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${a.image}` : a.image;
  const SITE = "https://visitrino.com";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: a.title,
        description: a.excerpt,
        image: absImg,
        author: { "@type": "Person", name: a.author, description: a.author_bio },
        publisher: { "@type": "Organization", name: "Visit RiNo", logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
        datePublished: a.created_at,
        dateModified: a.updated_at || a.created_at,
        mainEntityOfPage: `${SITE}/guides/${a.slug}`,
        keywords: (a.tags || []).join(", "),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
          { "@type": "ListItem", position: 3, name: a.title, item: `${SITE}/guides/${a.slug}` },
        ],
      },
    ],
  };

  return (
    <div>
      <Seo title={a.title} description={a.excerpt} image={absImg} type="article" jsonLd={jsonLd} canonicalPath={`/guides/${a.slug}`} />

      <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 text-xs text-neutral-500">
        <Link to="/" className="hover:text-white">Home</Link><span className="mx-1.5">/</span>
        <Link to="/guides" className="hover:text-white">Guides</Link><span className="mx-1.5">/</span>
        <span className="text-neutral-300 line-clamp-1">{a.title}</span>
      </nav>

      <div className="relative h-[44vh] overflow-hidden grain">
        <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 -mt-24 relative z-10">
        <Link to="/guides" className="inline-flex items-center gap-2 text-neutral-300 hover:text-white text-sm mb-6" data-testid="back-to-guides"><ArrowLeft size={16} /> Guides</Link>
        <span className="font-accent text-[#00E5FF] text-sm">{a.category}</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white mt-3 leading-tight">{a.title}</h1>
        <div className="flex items-center gap-3 mt-6 pb-8 border-b border-white/10">
          <img src={RANGER_AVATAR} alt={a.author} className="w-10 h-10 object-cover border border-[#FF0055]" />
          <div>
            <div className="text-white text-sm font-medium">{a.author}</div>
            <div className="text-neutral-500 text-xs flex items-center gap-1"><Clock size={11} /> {a.read_time}</div>
          </div>
          {a.ai_generated && <span className="ml-auto text-[10px] font-accent text-[#00E5FF] border border-[#00E5FF]/40 px-2 py-1">AI Written</span>}
        </div>
        <div className="py-8" data-testid="article-body"><Markdown content={a.body} /></div>

        {related.length > 0 && (
          <div className="pb-8" data-testid="guide-businesses">
            <h3 className="font-display font-bold text-xl text-white mb-4">Businesses in this guide</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((b) => (
                <Link key={b.id} to={`/directory/${b.slug}`} data-testid={`guide-biz-${b.slug}`}
                  className="flex items-center gap-3 border border-white/10 bg-[#141414] p-3 hover:border-[#FF0055] transition-colors group">
                  <img src={b.image} alt={b.name} className="w-14 h-14 object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium group-hover:text-[#FF0055] truncate">{b.name}</div>
                    <div className="text-neutral-500 text-xs truncate">{b.category}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {a.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-16">
            {a.tags.map((t) => <span key={t} className="border border-white/15 text-neutral-400 text-xs px-3 py-1.5">#{t}</span>)}
          </div>
        )}
      </article>
    </div>
  );
}
